import React from 'react';
import ReactDOM from 'react-dom';
import { MapPin, Award, Heart, Star, Bookmark, Folder, Plus, Check, X, Edit2, Trash2, Lock, Flag } from 'react-feather';
import { useResponsive } from '../hooks/useResponsive';
import { Pill, TextInput } from './ui';
import { rosterNeed } from '../utils/rosterNeed';
import RosterNeedBand from './RosterNeedBand';

export interface CustomListInfo {
  id: number;
  name: string;
  color: string;
  school_count: number;
}

interface SchoolCardProps {
  school: {
    school_id: number;
    name?: string | null;
    school_city?: string | null;
    school_state?: string | null;
    division?: string | null;
    conference?: string | null;
    logo_url?: string | null;
    match_score_norm?: number | null;
    roster_status?: string | null;
    sport?: string | null;
    head_coach?: string | null;
    head_coach_locked?: boolean; // coach exists but redacted for a free viewer
    acceptance_rate_pct?: number | null;
    cost_per_year_k?: number | null;
    openings?: number | null;
    undergraduate_student_count?: number | null;
    majors_count?: number | null;
    // Roster-need inputs (plan 3.1). position_openings ships on the All Schools
    // (/filter) response today, and on matched-for-you/open-rosters once attached.
    athlete_position?: string | null;
    athlete_graduation_year?: number | null;
    estimated_graduation_year?: number | null;
    position_openings?: Array<{ position_name?: string | null; count?: number | null; estimated_graduation_year?: number | null }>;
  };
  onCardClick?: (schoolId: number) => void;
  onAddToList?: (schoolId: number, listType: 'dream' | 'target' | 'safety') => void;
  schoolInteractions?: {
    heart: boolean;
    star: boolean;
    bookmark: boolean;
  };
  isLoading?: boolean;
  customLists?: CustomListInfo[];
  schoolCustomListIds?: number[];
  onToggleCustomList?: (schoolId: number, listId: number, isInList: boolean) => void;
  onCreateCustomList?: (schoolId: number, listName: string, color: string) => void;
  /** APS-260: shows "Report a change" on the coach line. Family pages only. */
  onReportCoach?: (schoolId: number) => void;
}

const CUSTOM_LIST_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#10B981',
  '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899',
  '#6B7280', '#151133'
];

const SchoolCard: React.FC<SchoolCardProps> = ({ 
  school, 
  onCardClick, 
  onAddToList,
  schoolInteractions = { heart: false, star: false, bookmark: false },
  isLoading = false,
  customLists = [],
  schoolCustomListIds = [],
  onToggleCustomList,
  onCreateCustomList,
  onReportCoach
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  // Which heart bucket the user just clicked — spin only that button, not all three.
  const [loadingBucket, setLoadingBucket] = React.useState<'dream' | 'target' | 'safety' | null>(null);
  React.useEffect(() => {
    if (!isLoading) setLoadingBucket(null);
  }, [isLoading]);
  const [showListDropdown, setShowListDropdown] = React.useState(false);
  const [isCreatingList, setIsCreatingList] = React.useState(false);
  const [newListName, setNewListName] = React.useState('');
  const [newListColor, setNewListColor] = React.useState('#3B82F6');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const folderBtnRef = React.useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = React.useState<{ top: number; left: number } | null>(null);
  const { isMobile } = useResponsive();
  // Some schools come back with no name. Show "School" so the card still renders.
  const displayName = (typeof school.name === 'string' && school.name.trim()) || 'School';
  const initial = displayName.charAt(0).toUpperCase();

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!showListDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowListDropdown(false);
        setIsCreatingList(false);
        setNewListName('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    // The dropdown is fixed-positioned from a one-time getBoundingClientRect, so
    // scrolling/resizing would leave it detached from the button — dismiss it.
    const dismiss = () => setShowListDropdown(false);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [showListDropdown]);

  const handleFolderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showListDropdown) {
      setShowListDropdown(false);
    } else {
      // Calculate position from the button
      if (folderBtnRef.current) {
        const rect = folderBtnRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.top - 8, // dropdown appears above the button
          left: rect.right - 220, // right-aligned, 220px wide
        });
      }
      setShowListDropdown(true);
    }
    setIsCreatingList(false);
    setNewListName('');
  };

  const handleToggleList = (e: React.MouseEvent, listId: number) => {
    e.stopPropagation();
    if (onToggleCustomList) {
      const isInList = schoolCustomListIds.includes(listId);
      onToggleCustomList(school.school_id, listId, isInList);
    }
  };

  const handleCreateList = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!newListName.trim()) return;
    if (onCreateCustomList) {
      onCreateCustomList(school.school_id, newListName.trim(), newListColor);
    }
    setNewListName('');
    setIsCreatingList(false);
  };

  const inAnyCustomList = schoolCustomListIds.length > 0;

  // Roster-need logic + presentation now live in ../utils/rosterNeed and
  // ./RosterNeedBand (shared with SchoolProfileModal — single source, no drift).

  // Handler for adding school to a list
  const handleListClick = (e: React.MouseEvent, listType: 'dream' | 'target' | 'safety') => {
    e.stopPropagation();
    setLoadingBucket(listType);
    if (onAddToList) {
      onAddToList(school.school_id, listType);
    }
  };

  const locationParts = [school.school_city, school.school_state].filter(Boolean);
  const hasLocation = locationParts.length > 0;
  const location = locationParts.join(', ');

  // Uniform stat row: ALWAYS render the same four commodity stats so every card is
  // the same height regardless of data coverage. A school missing Scorecard data
  // (JUCO/NAIA) shows "N/A" rather than collapsing the card and leaving dead space.
  // Openings is intentionally omitted — it already lives in the roster-need band
  // above (the moat), so we don't duplicate it here.
  const compact = (n: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  const stats: Array<{ label: string; value: string }> = [
    { label: 'Accept', value: school.acceptance_rate_pct != null ? `${school.acceptance_rate_pct}%` : 'N/A' },
    { label: 'Cost/Year', value: school.cost_per_year_k != null ? `$${school.cost_per_year_k}` : 'N/A' },
    { label: 'Undergrads', value: school.undergraduate_student_count ? compact(school.undergraduate_student_count) : 'N/A' },
    { label: 'Majors', value: school.majors_count && school.majors_count > 0 ? String(school.majors_count) : 'N/A' },
  ];

  // Fit badge (restored per request — the overall match quality families responded
  // to). Complements the concrete roster-need band below, which says *why*. Hidden
  // when there's no per-athlete score (staff / no-athlete surfaces).
  const fitScore = school.match_score_norm;
  const fitColor = (s: number) => (s >= 90 ? '#10B981' : s >= 75 ? '#059669' : s >= 50 ? '#F59E0B' : s >= 25 ? '#F97316' : '#6B7280');
  const fitLabel = (s: number) => (s >= 90 ? 'Excellent Fit' : s >= 75 ? 'Strong Fit' : s >= 50 ? 'Potential Fit' : s >= 25 ? 'Weak Fit' : 'Low Fit');

  // Compute the roster need once so the card and its band agree. A position+class
  // match ("Needs a 2028 pitcher") is the moat: the shared RosterNeedBand renders
  // it larger/stronger as the emphasis, and equal-height cards absorb the extra
  // band height so the matched card never ends up out of line with its row-mates.
  const need = rosterNeed(school);

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div
      role="button"
      tabIndex={0}
      aria-label={`View ${displayName} details`}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        // Full-height flex column so every card fills its (equal-height) grid cell
        // and no card is taller than its row-mates — kills the matched-card asymmetry.
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
      onClick={() => onCardClick && onCardClick(school.school_id)}
      onKeyDown={(e) => {
        // Keyboard access (#224): Enter/Space activate the card like a button.
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onCardClick && onCardClick(school.school_id);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header with Logo and Match Score */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        {/* Logo */}
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '8px',
          background: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          {school.logo_url ? (
            <>
              <img
                src={school.logo_url}
                alt={`${displayName} logo`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                // Dead/404 logo URLs render the browser's broken-image icon —
                // swap to the same letter fallback as the no-logo case.
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fb) fb.style.display = 'inline';
                }}
              />
              <span style={{
                display: 'none',
                fontSize: '24px',
                fontWeight: '700',
                color: '#9CA3AF'
              }}>
                {initial}
              </span>
            </>
          ) : (
            <span style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#9CA3AF'
            }}>
              {initial}
            </span>
          )}
        </div>

        {/* Fit badge — the overall match quality (restored). Sits with the concrete
            roster-need band below, which says *why* the fit is what it is. */}
        {fitScore != null && (
          <div style={{
            background: fitColor(fitScore),
            color: 'white',
            borderRadius: '10px',
            padding: '7px 12px',
            textAlign: 'center',
            minWidth: '78px',
            flexShrink: 0,
          }}>
            <div style={{ fontSize: '18px', fontWeight: 800, lineHeight: 1.1 }}>{Math.round(fitScore)}%</div>
            <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.02em', opacity: 0.95, textTransform: 'uppercase', marginTop: '1px' }}>
              {fitLabel(fitScore)}
            </div>
          </div>
        )}
      </div>

      {/* School Name */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#151133',
        margin: '0 0 8px 0',
        lineHeight: '1.3'
      }}>
        {displayName}
      </h3>

      {/* Location — always rendered (with a "Not listed" fallback) so every card has
          this row and stays the same height, even without city/state. */}
      <div style={{
        fontSize: '14px',
        color: hasLocation ? '#6B7280' : '#9CA3AF',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        {hasLocation ? location : 'Not listed'}
      </div>

      {/* Division & Conference */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px'
      }}>
        {school.division && <Pill tone="neutral">{school.division}</Pill>}
        {school.conference && <Pill tone="neutral">{school.conference}</Pill>}
      </div>

      {/* Stat row — always the same four columns (N/A when a value is missing) so
          every card is the same height, no matter the data coverage. */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '16px 0',
        borderTop: '1px solid #F3F4F6',
        borderBottom: '1px solid #F3F4F6',
        marginBottom: '16px',
        flexWrap: isMobile ? 'wrap' as const : 'nowrap' as const,
      }}>
        {stats.map((s) => {
          const isNA = s.value === 'N/A';
          return (
            <div key={s.label} style={{ flex: 1, textAlign: 'center', minWidth: isMobile ? '30%' : 0 }}>
              <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', whiteSpace: 'nowrap' }}>{s.label}</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: isNA ? '#D1D5DB' : '#374151' }}>{s.value}</div>
            </div>
          );
        })}
      </div>

      {/* Action Row: heart/list buttons on their own right-aligned row. marginTop:auto
          pins the bottom group so equal-height cards distribute slack in the middle. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: 'auto'
      }}>
        {/* Heart Pills + Custom List Button */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
          {/* 3 Hearts - Green. Hearts are the athlete's OWN interest/desire ranking
              (how much they want the school), NOT the computed fit score. */}
          <button
            onClick={(e) => handleListClick(e, 'dream')}
            aria-label="Save to your 3-heart interest list"
            title="Your interest — save & rank how much you want this school (separate from the fit score)"
            disabled={isLoading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: schoolInteractions.heart ? '#10B981' : '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '1';
            }}
          >
            {isLoading && loadingBucket === 'dream' ? (
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #E5E7EB',
                borderTop: '2px solid #10B981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <Heart 
                size={16} 
                color={schoolInteractions.heart ? 'white' : '#9CA3AF'}
                fill={schoolInteractions.heart ? 'white' : 'none'}
              />
            )}
          </button>

          {/* 2 Hearts - Orange (interest ranking, not fit) */}
          <button
            onClick={(e) => handleListClick(e, 'target')}
            aria-label="Save to your 2-heart interest list"
            title="Your interest — save & rank how much you want this school (separate from the fit score)"
            disabled={isLoading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: (schoolInteractions.heart || schoolInteractions.star) ? 
                (schoolInteractions.heart ? '#10B981' : '#F59E0B') : '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '1';
            }}
          >
            {isLoading && loadingBucket === 'target' ? (
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #E5E7EB',
                borderTop: '2px solid #F59E0B',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <Heart 
                size={16} 
                color={(schoolInteractions.heart || schoolInteractions.star) ? 'white' : '#9CA3AF'}
                fill={(schoolInteractions.heart || schoolInteractions.star) ? 'white' : 'none'}
              />
            )}
          </button>

          {/* 1 Heart - Red (interest ranking, not fit) */}
          <button
            onClick={(e) => handleListClick(e, 'safety')}
            aria-label="Save to your 1-heart interest list"
            title="Your interest — save & rank how much you want this school (separate from the fit score)"
            disabled={isLoading}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: (schoolInteractions.heart || schoolInteractions.star || schoolInteractions.bookmark) ? 
                (schoolInteractions.heart ? '#10B981' : schoolInteractions.star ? '#F59E0B' : '#DC2626') : '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.currentTarget.style.opacity = '1';
            }}
          >
            {isLoading && loadingBucket === 'safety' ? (
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #E5E7EB',
                borderTop: '2px solid #DC2626',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <Heart 
                size={16} 
                color={(schoolInteractions.heart || schoolInteractions.star || schoolInteractions.bookmark) ? 'white' : '#9CA3AF'}
                fill={(schoolInteractions.heart || schoolInteractions.star || schoolInteractions.bookmark) ? 'white' : 'none'}
              />
            )}
          </button>

          {/* Custom List Folder Button */}
          <div style={{ position: 'relative', marginLeft: '2px' }}>
            <button
              ref={folderBtnRef}
              onClick={handleFolderClick}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                border: 'none',
                background: inAnyCustomList ? '#3B82F6' : '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Folder size={16} color={inAnyCustomList ? 'white' : '#9CA3AF'} fill={inAnyCustomList ? 'white' : 'none'} />
              {inAnyCustomList && (
                <div style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: '#3B82F6', border: '2px solid white',
                  fontSize: '9px', fontWeight: '700', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {schoolCustomListIds.length}
                </div>
              )}
            </button>

            {/* Dropdown via portal */}
            {showListDropdown && ReactDOM.createPortal(
              <div ref={dropdownRef} style={{
                position: 'fixed',
                bottom: dropdownPos ? `calc(100vh - ${dropdownPos.top}px)` : undefined,
                left: dropdownPos ? `${dropdownPos.left}px` : undefined,
                width: '220px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                border: '1px solid #E5E7EB',
                zIndex: 10000,
                // The menu opens above the button. Keep it on screen and let it scroll.
                maxHeight: dropdownPos ? `${Math.max(dropdownPos.top - 12, 200)}px` : undefined,
                overflowX: 'hidden',
                overflowY: 'auto'
              }}>
                {/* One membership surface: the 3 interest (heart) lists pinned on
                    top, then the user's folders below — so this dropdown is the
                    single place to see/manage every list a school is in. The
                    quick heart buttons on the card are shortcuts to these. */}
                <div style={{ padding: '10px 12px 6px', fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Interest
                </div>
                {([
                  { bucket: 'dream' as const, on: schoolInteractions.heart, color: '#10B981', label: '3-heart interest' },
                  { bucket: 'target' as const, on: schoolInteractions.star, color: '#F59E0B', label: '2-heart interest' },
                  { bucket: 'safety' as const, on: schoolInteractions.bookmark, color: '#DC2626', label: '1-heart interest' },
                ]).map(row => (
                  <button
                    key={row.bucket}
                    onClick={(e) => handleListClick(e, row.bucket)}
                    disabled={isLoading}
                    aria-label={`Set ${row.label}`}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      background: row.on ? '#F0FDF4' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'background 0.15s',
                      textAlign: 'left',
                      opacity: isLoading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => { if (!row.on && !isLoading) e.currentTarget.style.background = '#F9FAFB'; }}
                    onMouseLeave={(e) => { if (!row.on) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Heart size={14} color={row.color} fill={row.on ? row.color : 'none'} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.label}
                    </span>
                    {row.on && <Check size={14} color={row.color} strokeWidth={3} />}
                  </button>
                ))}

                {/* Divider */}
                <div style={{ height: '1px', background: '#E5E7EB', margin: '6px 0' }} />

                <div style={{ padding: '4px 12px 6px', fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Your lists
                </div>

                <div style={{ maxHeight: '276px', overflowY: 'auto' }}>
                  {customLists.length === 0 && !isCreatingList && (
                    <div style={{ padding: '12px 16px', fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
                      No lists yet
                    </div>
                  )}

                  {customLists.map(list => {
                    const isInList = schoolCustomListIds.includes(list.id);
                    return (
                      <button
                        key={list.id}
                        onClick={(e) => handleToggleList(e, list.id)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: 'none',
                          background: isInList ? '#EFF6FF' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          cursor: 'pointer',
                          transition: 'background 0.15s',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => { if (!isInList) e.currentTarget.style.background = '#F9FAFB'; }}
                        onMouseLeave={(e) => { if (!isInList) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          width: '10px', height: '10px', borderRadius: '3px',
                          background: list.color, flexShrink: 0
                        }} />
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {list.name}
                        </span>
                        {isInList && <Check size={14} color="#3B82F6" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: '#E5E7EB' }} />

                {/* Create New List */}
                {isCreatingList ? (
                  <div style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                    {/* The hand-rolled focus treatment here was an inline
                      * `outline: none` plus onFocus/onBlur border swaps. Both
                      * go: the global :focus-visible ring is the indicator now.
                      * The border no longer tints — `.aps-input:focus` in
                      * globals.css says it should, but the primitive sets
                      * `border` inline and inline wins, as that rule notes. */}
                    <TextInput
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateList(e as any); if (e.key === 'Escape') { setIsCreatingList(false); setNewListName(''); } }}
                      placeholder="List name..."
                      aria-label="New list name"
                      autoFocus
                      style={{
                        padding: '6px 8px',
                        fontSize: '13px',
                        marginBottom: '8px',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {CUSTOM_LIST_COLORS.map(c => (
                        <button
                          key={c}
                          onClick={(e) => { e.stopPropagation(); setNewListColor(c); }}
                          style={{
                            width: '20px', height: '20px', borderRadius: '4px',
                            background: c, border: newListColor === c ? '2px solid #151133' : '2px solid transparent',
                            cursor: 'pointer', padding: 0
                          }}
                        />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={handleCreateList}
                        disabled={!newListName.trim()}
                        style={{
                          flex: 1, padding: '6px', border: 'none', borderRadius: '6px',
                          background: newListName.trim() ? '#3B82F6' : '#E5E7EB',
                          color: newListName.trim() ? 'white' : '#9CA3AF',
                          fontSize: '12px', fontWeight: '600', cursor: newListName.trim() ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Create
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsCreatingList(false); setNewListName(''); }}
                        style={{
                          padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: '6px',
                          background: 'white', color: '#6B7280', fontSize: '12px', fontWeight: '500', cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsCreatingList(true); }}
                    style={{
                      width: '100%', padding: '10px 12px', border: 'none',
                      background: 'transparent', display: 'flex', alignItems: 'center', gap: '8px',
                      cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#3B82F6',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F7FF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    New List
                  </button>
                )}
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Head Coach — always rendered (with a fallback) so every card ends the same
          way and stays the same height, even when we don't have a coach name. */}
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #E5E7EB',
        fontSize: '13px',
        color: '#6B7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
      }}>
        <div style={{ minWidth: 0 }}>
          <strong style={{ color: '#374151' }}>Coach:</strong> {school.head_coach || (
            school.head_coach_locked
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#F9550D', fontWeight: 600 }}><Lock size={12} /> Locked</span>
              : 'Not listed'
          )}
        </div>
        {onReportCoach && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReportCoach(school.school_id); }}
            aria-label={`Report a change to the ${displayName} coaches`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
              padding: '4px 2px', margin: '-4px -2px', background: 'transparent', border: 'none',
              fontSize: '12px', fontWeight: 600, color: 'var(--aps-text-2)',
              textDecoration: 'underline', textUnderlineOffset: '3px',
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap'
            }}
          >
            <Flag size={12} aria-hidden="true" />
            Report a change
          </button>
        )}
      </div>

      {/* Roster-need status as the card footer — a thin, full-width green/amber/red
          bar carrying the full detail (no truncation). Shared tone scale + formatter. */}
      {need && <RosterNeedBand need={need} compact style={{ marginTop: '12px' }} />}
    </div>
    </>
  );
};

// React.memo (#146): the Dashboard/CollegeLists grids re-render on every parent
// state change (incl. per-keystroke search). Memoizing skips re-rendering cards
// whose props are unchanged, so a 21-card grid no longer re-renders wholesale.
export default React.memo(SchoolCard);
